import { useState } from "react";

/**
 * Phase 21 — "Aura Text For Me" confirmation card.
 *
 * Rendered inline beside Aura's reply when `pending_action.type === "send_text"`.
 * The user can edit the draft before sending, then tap "Open Messages" which
 * triggers an `sms:` URI — opens the device's native Messages app pre-filled
 * with the recipient + body. We never send the SMS ourselves: zero Twilio
 * cost, zero compliance risk.
 *
 * Works natively on iOS Safari and Android Chrome (sms: URI scheme support).
 */
export function SendTextConfirmCard({ pending, onSent, onCancelled }) {
  const [draft, setDraft] = useState(pending?.draft || "");
  const [opened, setOpened] = useState(false);

  const phone = pending?.recipient_phone || "";
  const name = pending?.recipient_name || "them";

  // sms: URI — body param uses ?body= on iOS (most platforms), ?body= also works on Android Chrome.
  // We URL-encode the draft so emojis + line breaks survive.
  const buildSmsHref = () => {
    if (!phone) return "";
    const cleanPhone = phone.replace(/[^\d+]/g, "");
    return `sms:${cleanPhone}?&body=${encodeURIComponent(draft)}`;
  };

  if (pending?._resolved === "sent") {
    return (
      <div className="text-confirm-card text-confirm-sent" data-testid="text-confirm-sent">
        <span>📨 Opened your Messages app — tap send when ready.</span>
      </div>
    );
  }
  if (pending?._resolved === "cancelled") {
    return (
      <div className="text-confirm-card text-confirm-cancelled" data-testid="text-confirm-cancelled">
        <span>Cancelled — no draft kept.</span>
      </div>
    );
  }

  return (
    <div className="text-confirm-card" data-testid="text-confirm-card">
      <div className="text-confirm-header">
        <span className="text-confirm-icon">💬</span>
        <div className="text-confirm-meta">
          <strong>Draft text to {name}</strong>
          {phone && <span>{phone}</span>}
        </div>
      </div>
      <textarea
        className="text-confirm-draft"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        maxLength={500}
        data-testid="text-confirm-draft-input"
        placeholder="Edit the draft if you want..."
      />
      <div className="text-confirm-meta-sub">
        <span>{draft.length}/500 · You'll tap send in Messages</span>
      </div>
      <div className="text-confirm-actions">
        <button
          className="ghost-btn small"
          onClick={() => onCancelled && onCancelled()}
          data-testid="text-confirm-cancel"
        >
          Nevermind
        </button>
        <a
          href={buildSmsHref()}
          className="init-btn text-confirm-open"
          onClick={() => {
            setOpened(true);
            if (onSent) setTimeout(onSent, 200);
          }}
          data-testid="text-confirm-open-messages"
        >
          {opened ? "Opening…" : "📱 Open Messages"}
        </a>
      </div>
    </div>
  );
}

export default SendTextConfirmCard;
